// Opening a timer's FORM window, and waiting for an old-bundle window to
// acknowledge a command.  One-click timers (docs/specs/timers-one-click-spec.md,
// Track C): shared by the Timers panel in the desktop main window, pop-out
// pages (after a headless start) and the floating bar's ↗ — moved out of
// pages/TimerBar.js (openSlot / awaitAck) so every caller follows the same
// "never two windows on one timer" rules:
//
//   • an owner record with a popupKey → electronAPI.focusPopupWindow(pk),
//     falling back to reopening under THAT key (the desktop app turns an
//     open under an existing key into a focus);
//   • held by a window that recorded no popupKey (an older page) → a 'focus'
//     command; only if it never answers is it reopened, and then only once
//     nothing holds it any more (a guessed key would open a second window);
//   • otherwise → a new window on the slot under popupKeyForSlot(key) —
//     'timer-resume-<id>' for a resume slot, so a second click focuses it.
//
// Every route goes through tagRouteWithPopupKey (?pk=) so the window records
// the key it was opened under.  Browser-only callers get { how:'unsupported' }
// and show the in-page form instead.  openFormAfterHeldPause: the same, after
// a manual pause sent to an old-bundle window as a command.

import { safeOpenPopupWindow } from '../services/token'
import { timerWindowOptions } from '../utils/desktopVersion'
import { COMMAND_KEY, parseCommand, sendTimerCommand } from './timerCommands'
import { MESSAGES } from './timerStore'
import {
  isLegacySlotKey, isTimerSlotKey, isV2, ownerState, parseSlot, popupKeyForSlot, readOwner,
  reopenRouteForSlot, tagRouteWithPopupKey
} from './timerSlots'

// How long a sender waits for a window to ACKNOWLEDGE a command before acting
// on the silence (frozen, mid-reload, crashed, or an old page that cannot
// hear it).
export const COMMAND_ACK_MS = 1500

const windowStorage = () => {
  try { return typeof window !== 'undefined' ? window.localStorage : null } catch { return null }
}
const windowElectron = () => {
  try { return typeof window !== 'undefined' ? window.electronAPI : undefined } catch { return undefined }
}

// Wait for the window holding `storageKey` to acknowledge `cmd`; if it does
// not within `ms`, call onSilence(ownerRecordNow).  A slot saved or discarded
// meanwhile needs nothing.  `pending` (a Map) supersedes an older wait on the
// same slot, so a quick pause-then-resume never reads the first click's reply
// as missing.  Returns a cancel function.
export function awaitAck ({ storage, storageKey, cmd, onSilence, ms = COMMAND_ACK_MS, pending = null } = {}) {
  if (!cmd || !storageKey || typeof onSilence !== 'function') return () => {}
  const s = storage || windowStorage()
  if (pending) {
    const prev = pending.get(storageKey)
    if (prev) clearTimeout(prev)
  }
  const t = setTimeout(() => {
    if (pending && pending.get(storageKey) === t) pending.delete(storageKey)
    let still = null
    try { still = parseSlot(s.getItem(storageKey)) } catch { still = null }
    if (!still) return
    const owner = readOwner(s, storageKey)
    if (owner && owner.ack === cmd.id) return
    try { onSilence(owner) } catch { /* a caller's handler must not throw out of a timer */ }
  }, ms)
  if (pending) pending.set(storageKey, t)
  return () => {
    clearTimeout(t)
    if (pending && pending.get(storageKey) === t) pending.delete(storageKey)
  }
}

// (Re)open the form window for slot `key` under popupKey `pk`.
export function reopenTimerWindow (key, pk, { open = safeOpenPopupWindow } = {}) {
  if (!isTimerSlotKey(key) || !pk) return null
  return open(pk, tagRouteWithPopupKey(reopenRouteForSlot(key), pk), timerWindowOptions())
}

// Bring timer `key`'s form forward, or open one on its slot.  Resolves
// { how } — 'focused' | 'reopened' | 'command' | 'opened' | 'legacy-focus' |
// 'missing' | 'invalid' | 'unsupported' — plus pk / cmd where relevant.
//   storage, electronAPI, now, open (safeOpenPopupWindow), sendCommand and
//   pending are injectable for tests and for the bar's per-slot ack map.
export async function openTimerForm (key, {
  storage, electronAPI, now, pending = null,
  open = safeOpenPopupWindow, sendCommand = sendTimerCommand
} = {}) {
  if (!isTimerSlotKey(key)) return { how: 'invalid' }
  const e = electronAPI !== undefined ? electronAPI : windowElectron()
  if (!e || typeof e.openPopupWindow !== 'function') return { how: 'unsupported' }
  const s = storage || windowStorage()
  if (!s) return { how: 'unsupported' }
  let slot = null
  try { slot = parseSlot(s.getItem(key)) } catch { slot = null }
  if (!slot) return { how: 'missing' }
  const reopen = (pk) => reopenTimerWindow(key, pk, { open })
  // A legacy slot still on old code belongs to the main window's in-page
  // timer: ask it to show itself.  Once adopted (v2) it is an ordinary slot
  // and opens in a pop-out like any other.
  if (isLegacySlotKey(key) && !isV2(slot)) {
    const cmd = sendCommand(key, 'focus', { storage: s })
    return { how: 'legacy-focus', cmd }
  }
  const t = now != null ? now : Date.now()
  const owner = readOwner(s, key)
  const st = ownerState(owner, t)
  if (owner && owner.pk) {
    if (typeof e.focusPopupWindow === 'function') {
      try {
        const r = await e.focusPopupWindow(owner.pk)
        if (r && r.focused) return { how: 'focused', pk: owner.pk }
      } catch { /* fall through and reopen under the same key */ }
    }
    reopen(owner.pk)
    return { how: 'reopened', pk: owner.pk }
  }
  if (st === 'live' || st === 'quiet') {
    const cmd = sendCommand(key, 'focus', { storage: s })
    if (cmd) {
      awaitAck({
        storage: s,
        storageKey: key,
        cmd,
        pending,
        onSilence: (ownerNow) => {
          if (ownerNow && ownerNow.pk) { reopen(ownerNow.pk); return }
          const stNow = ownerState(ownerNow, Date.now())
          if (stNow === 'live' || stNow === 'quiet') { sendCommand(key, 'focus', { storage: s }); return }
          reopen(popupKeyForSlot(key))
        }
      })
    }
    return { how: 'command', cmd }
  }
  const pk = popupKeyForSlot(key)
  reopen(pk)
  return { how: 'opened', pk }
}

// A beat past COMMAND_ACK_MS, so the pause's own ack wait has run first.
const AFTER_ACK_MS = 100

// The person's ⏸ on a timer an OLD-bundle window holds (v1-held) was sent to
// that window as a 'pause' command; that window IS the timer's form, so bring
// it forward ("when you hit pause, the timer entry window pops up" — James,
// 2026-10-15).  A window acknowledges only its LAST command (owner.ack), so a
// 'focus' sent beside the pause would make the pause's ack wait read silence
// and warn falsely ("Pause … in its window").  Hence:
//   • the window recorded a popupKey → openTimerForm now (focusPopupWindow,
//     no command);
//   • none → wait out the pause's ack window, then openTimerForm (a 'focus'
//     command) only if the pause was acknowledged AND the one command key
//     still holds it — a newer command (a ▶ on it, another timer's) is never
//     overwritten.  On silence the pause's own wait has already brought the
//     window forward.
// Resolves openTimerForm's { how }, or { how: 'silent' | 'superseded' |
// 'invalid' | 'failed' }.
// Pass `pending` (the bar's per-slot ack map) through to openTimerForm.
export function openFormAfterHeldPause (key, cmd, { storage, ms = COMMAND_ACK_MS, ...rest } = {}) {
  const s = storage || windowStorage()
  if (!s || !isTimerSlotKey(key)) return Promise.resolve({ how: 'invalid' })
  const owner = readOwner(s, key)
  if (!cmd || !cmd.id || (owner && owner.pk)) return openTimerForm(key, { storage: s, ...rest })
  return new Promise((resolve) => {
    setTimeout(() => {
      const now = readOwner(s, key)
      if (!now || now.ack !== cmd.id) { resolve({ how: 'silent' }); return }
      let last = null
      try { last = parseCommand(s.getItem(COMMAND_KEY)) } catch { last = null }
      if (!last || last.id !== cmd.id) { resolve({ how: 'superseded' }); return }
      openTimerForm(key, { storage: s, ...rest }).then(resolve, () => resolve({ how: 'failed' }))
    }, ms + AFTER_ACK_MS)
  })
}

// After timerStore.startNewTimer / startOrResume / pause: the old-bundle
// windows sent a 'pause' (or 'resume') command — result.held — must
// acknowledge it.  On silence, bring that window forward (by its popupKey, or
// a 'focus' command when it recorded none) and call
// onSilence({ key, label, pk, message }) with "Pause "<label>" in its window"
// — reported, never treated as success.  Returns a cancel function.
export function awaitHeldPauses (held, { storage, onSilence, electronAPI, ms = COMMAND_ACK_MS, pending = null, sendCommand = sendTimerCommand } = {}) {
  const list = Array.isArray(held) ? held : []
  const s = storage || windowStorage()
  const cancels = list.filter(h => h && h.key && h.cmd).map(h => awaitAck({
    storage: s,
    storageKey: h.key,
    cmd: h.cmd,
    ms,
    pending,
    onSilence: (ownerNow) => {
      const pk = (ownerNow && ownerNow.pk) || h.pk || null
      const e = electronAPI !== undefined ? electronAPI : windowElectron()
      if (pk && e && typeof e.focusPopupWindow === 'function') {
        try { Promise.resolve(e.focusPopupWindow(pk)).catch(() => {}) } catch { /* best effort */ }
      } else {
        sendCommand(h.key, 'focus', { storage: s })
      }
      const message = h.cmd.action === 'resume' ? MESSAGES.heldResume(h.label) : MESSAGES.heldPause(h.label)
      if (typeof onSilence === 'function') onSilence({ key: h.key, label: h.label, pk, message })
    }
  }))
  return () => cancels.forEach(c => c())
}
